"use client"

import { memo, useState, useEffect } from "react"

import { QuestionResponse } from "@/services/practice"
import { Button } from "@/components/ui/button"
import { Flag, MessageSquare, Bookmark, CheckCircle, X } from "lucide-react"
import { cn, btn3d } from "@/lib/utils"
import { toast } from "sonner"
import { MathRenderer } from "@/components/ui/math-renderer"

interface AnswerSectionProps {
    question: QuestionResponse;
    selectedAnswer: string | null;
    onMainAnswerChange: (value: string) => void;
    submitted?: boolean;
    isCorrect?: boolean;
    correctAnswer?: string;
    explanation?: string;
    isMarked?: boolean;
    onMark?: () => void;
    onReport?: () => void;
    onFeedback?: () => void;
    hideActionBar?: boolean;
}

export const AnswerSection = memo(function AnswerSection({
    question,
    selectedAnswer,
    onMainAnswerChange,
    submitted,
    isCorrect,
    correctAnswer,
    explanation,
    isMarked,
    onMark,
    onReport,
    onFeedback,
    hideActionBar
}: AnswerSectionProps) {
    const isMCQ = question.questionType === 'mcq';
    const [struckOut, setStruckOut] = useState<Set<string>>(new Set());

    // Reset crossed-out choices when the question changes
    useEffect(() => {
        setStruckOut(new Set());
    }, [question.id]);

    function toggleStrike(key: string, e: React.MouseEvent) {
        e.stopPropagation();
        setStruckOut(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }

    return (
        <div className="space-y-6 relative z-10 pointer-events-auto">
            {/* Action Bar */}
            {!hideActionBar && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        {question.userContext?.isMastered ? (
                            <Button variant="outline" size="sm" className="h-8 text-base gap-1.5 border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-300 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20">
                                <CheckCircle className="h-3.5 w-3.5 fill-current" />
                                Mastered
                            </Button>
                        ) : (
                            <Button variant="outline" size="sm" className={cn(
                                "h-9 text-base gap-1.5",
                                question.userContext?.attemptCount && question.userContext?.attemptCount > 0
                                    ? "border-yellow-200 bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:border-yellow-300 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/20"
                                    : "border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20"
                            )}>
                                <span className={cn(
                                    "w-1.5 h-1.5 rounded-full inline-block",
                                    question.userContext?.attemptCount && question.userContext?.attemptCount > 0
                                        ? "bg-yellow-500"
                                        : "bg-red-500"
                                )} />
                                {question.userContext?.attemptCount && question.userContext?.attemptCount > 0
                                    ? `Tried ${question.userContext.attemptCount} times`
                                    : "Not Tried"}
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onMark}
                            className={cn(
                                "h-8 text-base gap-1.5 cursor-pointer relative z-20",
                                btn3d,
                                isMarked
                                    ? "text-purple-600 border-purple-200 bg-purple-50 hover:bg-purple-100 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20"
                                    : "bg-card text-muted-foreground hover:text-purple-600"
                            )}
                        >
                            <Bookmark className={cn("h-3.5 w-3.5", isMarked && "fill-current")} />
                            {isMarked ? "Marked for Review" : "Mark for Review"}
                        </Button>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            size="sm"
                            className={cn("h-8 text-base gap-1.5 border-primary/20 text-primary/80 bg-primary/5 hover:text-primary hover:border-primary/50 hover:bg-primary/10 relative z-20", btn3d)}
                            onClick={onFeedback}
                        >
                            <MessageSquare className="h-3.5 w-3.5" />
                            Help
                            {question.feedbackCount && question.feedbackCount > 0 && (
                                <span className="ml-0.5 inline-flex items-center justify-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                                    {question.feedbackCount}
                                </span>
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className={cn("h-8 text-base gap-1.5 bg-card text-muted-foreground hover:text-red-600 hover:border-red-200 hover:bg-red-50 relative z-20", btn3d)}
                            onClick={onReport}
                        >
                            <Flag className="h-3.5 w-3.5" />
                            Report
                        </Button>
                    </div>
                </div>
            )}

            {/* Options */}
            {isMCQ && question.mcqOptions ? (
                <div className="space-y-3">
                    {Object.entries(question.mcqOptions).map(([key, value]) => {
                        let optionStyle = "border-border bg-card";
                        let keyStyle = "border-muted-foreground/30 text-muted-foreground";

                        // Default selection style
                        if (selectedAnswer === key) {
                            optionStyle = "border-primary bg-primary/5 ring-1 ring-primary";
                            keyStyle = "bg-primary text-primary-foreground border-primary";
                        }

                        // Feedback styles (override default)
                        if (submitted) {
                            const isSelected = selectedAnswer === key;
                            const isTheCorrectAnswer = key === correctAnswer;

                            if (isSelected) {
                                if (isCorrect) {
                                    optionStyle = "border-green-500 bg-green-50 ring-1 ring-green-500 dark:bg-green-500/10";
                                    keyStyle = "bg-green-500 text-white border-green-500";
                                } else {
                                    optionStyle = "border-red-500 bg-red-50 ring-1 ring-red-500 dark:bg-red-500/10";
                                    keyStyle = "bg-red-500 text-white border-red-500";
                                }
                            } else if (isTheCorrectAnswer) {
                                // Show correct answer if user got it wrong
                                optionStyle = "border-green-500 bg-green-50 ring-1 ring-green-500 dark:bg-green-500/10";
                                keyStyle = "bg-green-500 text-white border-green-500";
                            }
                        }

                        const isStruck = !submitted && struckOut.has(key);

                        return (
                            <div key={key} className="flex items-center gap-2">
                                <button
                                    onClick={() => !submitted && !isStruck && onMainAnswerChange(key)}
                                    disabled={submitted}
                                    className={cn(
                                        "flex-1 flex items-start p-4 rounded-xl border text-left transition-all hover:bg-muted/50 cursor-pointer relative z-20",
                                        submitted ? "cursor-default" : "",
                                        isStruck ? "opacity-40" : "",
                                        optionStyle
                                    )}
                                >
                                    <div className={cn(
                                        "flex items-center justify-center w-8 h-8 rounded-full border text-lg sm:text-xl font-medium mr-4 shrink-0 transition-colors",
                                        keyStyle
                                    )}>
                                        {key}
                                    </div>
                                    <div className={cn(
                                        "text-lg sm:text-xl pt-0.5 font-serif flex-1 w-full overflow-hidden",
                                        isStruck && "line-through decoration-foreground/60 decoration-2"
                                    )}>
                                        <MathRenderer content={value} />
                                    </div>
                                </button>

                                {/* Eliminate / un-eliminate — always visible, outside the card */}
                                {!submitted && (
                                    <button
                                        onClick={(e) => toggleStrike(key, e)}
                                        title={isStruck ? "Restore choice" : "Eliminate choice"}
                                        className={cn(
                                            "shrink-0 flex items-center justify-center w-7 h-7 rounded-full border transition-all z-30",
                                            isStruck
                                                ? "border-foreground/50 bg-foreground/10 text-foreground/70 hover:bg-foreground/20"
                                                : "border-muted-foreground/40 bg-background text-muted-foreground hover:border-destructive/60 hover:text-destructive hover:bg-destructive/10"
                                        )}
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div className="space-y-2">
                    <label className="text-sm font-medium">Your Answer:</label>
                    <input
                        type="text"
                        disabled={submitted && isCorrect}
                        className={cn(
                            "w-full p-3 rounded-md border bg-background relative z-20",
                            submitted && isCorrect && "border-green-500 bg-green-50 text-green-900 dark:bg-green-500/10 dark:text-green-400",
                            submitted && !isCorrect && "border-red-500 bg-red-50 text-red-900 dark:bg-red-500/10 dark:text-red-400"
                        )}
                        placeholder="Enter your answer..."
                        value={selectedAnswer || ""}
                        onChange={(e) => onMainAnswerChange(e.target.value)}
                    />
                </div>
            )}


        </div>
    )
})

AnswerSection.displayName = "AnswerSection"
